
// Serializes digitized Knude/Ledning/Komponent features (see the
// Digitizing module - a feature INSTANCE's own {name, coords, attributes})
// back out as DANVA XML, for every format DANVAParser.js can also read -
// the older "Group" generation (DANVAND 1.1.0, DANDAS 2.5.2/2.6.0) via
// buildKnudeGroupXml/buildLedningGroupXml/buildKnudeKomponentGroupXml
// below, and the newer "Samling"/WKT generation (DANVAND 2.1.1, DANDAS
// 3.1.1) via buildKnudeSamlingXml/buildLedningSamlingXml further down -
// see DANVAParser.js's own header comment for what each generation looks
// like and why DANVAND 2.0.0 specifically is out of scope (LE34: never the
// format actually used). This is deliberately NOT a full field-by-field
// schema implementation - each element only carries the handful of fields
// real DANVA exports from this codebase's own example data always
// populate (see DANVA_FIELD_ALIASES below), matched case/spacing-
// insensitively against whatever attribute names the user's own feature-
// type happens to use, plus the geometry itself. A required field with
// nothing matched falls back to the same default every LE34 export in the
// example data already uses (StatusKode "2" = udført, EjerforholdKode
// "10", etc.) rather than leaving it empty or failing the export outright.
//
// The batch entry point - scanning every digitized instance across every
// feature-type at once and deciding which of the builders below each one
// belongs in - is index.html's own exportAllDanvaData(), not this file:
// it derives family (vand/afløb) from each instance's own saved `supply`
// and Knude/Ledning/Komponent from its geometry type (point -> Knude, +
// Komponent when vand and the target DANVAND version still has that
// concept; line -> Ledning), then calls the pure builders here per family/
// version. This file only ever builds ONE already-grouped XML document at
// a time; it has no notion of "all of a project's digitized data" itself.

function escapeXml(value){
	return String(value)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function el(tag, value){
	if(value === null || value === undefined || value === ""){
		return "";
	}
	return `<${tag}>${escapeXml(value)}</${tag}>`;
}

// Matches an alias list against an instance's own attributes object
// (case/space/underscore-insensitive - a user-authored feature-type's
// attribute names are never guaranteed to match a DANVA field name
// exactly), falling back to `fallback` (a literal, or a zero-arg function
// for something computed per-call like "now").
function normalizeKey(key){
	return key.toLowerCase().replace(/[\s_-]/g, "");
}

function findAttr(attributes, aliases){
	if(!attributes){
		return null;
	}
	let byNormalized = new Map(Object.entries(attributes).map(([k, v]) => [normalizeKey(k), v]));
	for(let alias of aliases){
		let value = byNormalized.get(normalizeKey(alias));
		if(value !== undefined && value !== null && value !== ""){
			return value;
		}
	}
	return null;
}

function resolveField(attributes, aliases, fallback){
	let found = findAttr(attributes, aliases);
	if(found !== null){
		return found;
	}
	return typeof fallback === "function" ? fallback() : fallback ?? null;
}

const NOW_ISO = () => new Date().toISOString();
const NOW_DATE = () => new Date().toISOString().slice(0, 10);

// RFC4122 v4 UUID, matching kerne KER_GuidType.xsd's own required pattern
// (strict hex-with-dashes, either case). Digitized data has no real DANVA
// registry identity of its own, so a knude's guid is generated once per
// knude NAME and reused everywhere that name is referenced again in the
// same export (ensureDanvaGuid, below) - not a real registry guid, but a
// stable, schema-valid one; the same approach the parallel DANVAND 2.0.0
// exporter elsewhere in this codebase (src/digitization/danva/
// DanvaExchangeAdapter.js's generateDanvaGuid) already settled on for the
// identical problem.
function randomGuid(){
	if(typeof crypto !== "undefined" && crypto.randomUUID){
		return crypto.randomUUID();
	}
	let bytes = new Array(16).fill(0).map(() => Math.floor(Math.random() * 256));
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	let hex = bytes.map(b => b.toString(16).padStart(2, "0"));
	return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
}

// `guidsByKnudenavn` is one Map shared across a whole export call so a
// knude referenced by several ledninger, or a ledning's own start/slut
// knude, always resolves to the same guid.
function ensureDanvaGuid(guidsByKnudenavn, knudenavn, existingGuid){
	if(existingGuid){
		guidsByKnudenavn.set(knudenavn, existingGuid);
		return existingGuid;
	}
	if(!guidsByKnudenavn.has(knudenavn)){
		guidsByKnudenavn.set(knudenavn, randomGuid());
	}
	return guidsByKnudenavn.get(knudenavn);
}

// WKT ("Samling"-generation geometry - see DANVAParser.js's own matching
// comment on why DANVAND 2.1.1/DANDAS 3.1.1 use this instead of separate
// X/Y/Z elements). Always written with an explicit "Z" tag and full-
// precision coordinates, so DANVAParser.js's own parseWktCoords round-trips
// it exactly.
function buildWktPoint([x, y, z]){
	return `POINT Z (${x} ${y} ${z ?? 0})`;
}

function buildWktLineString(coords){
	return `LINESTRING Z (${coords.map(([x, y, z]) => `${x} ${y} ${z ?? 0}`).join(", ")})`;
}

// Default FunktionStruktur role when nothing on the digitized instance
// says otherwise - "the most generic real role a node can have" for each
// family, since FunktionStruktur is a required-if-present single choice
// (see DANVAParser.js's readFunktionStruktur) and inventing a MORE
// specific role (Ventil, Pumpested, ...) out of nowhere would be a false
// domain claim, the same reasoning this project's DANVA work has applied
// elsewhere (see DANVAParser.js's header comment on the DANVAND 2.0.0
// FunktionStruktur schema defect this project chose not to paper over).
const DEFAULT_SAMLING_SUBTYPE = {danvand: "Tilslutningspunkt", dandas: "Broend"};

// item: {knudenavn, x, y, z, attributes}
function knudeElementXml(family, item){
	let a = item.attributes;
	let name = escapeXml(item.knudenavn);

	if(family === "danvand"){
		return `  <Knude Knudenavn="${name}">
${el("XKoord", item.x.toFixed(2))}
${el("YKoord", item.y.toFixed(2))}
${el("Kote", item.z.toFixed(2))}
${el("StatusKode", resolveField(a, ["StatusKode", "Status"], "2"))}
${el("EjerforholdKode", resolveField(a, ["EjerforholdKode", "Ejerforhold"], "10"))}
${el("DatoEtableret", resolveField(a, ["DatoEtableret", "Dato"], NOW_ISO))}
${el("Sagsnummer", resolveField(a, ["Sagsnummer", "Sag"], null))}
    <NoteItems />
  </Knude>`;
	}

	// dandas
	return `  <Knude Knudenavn="${name}">
${el("KnudeKode", resolveField(a, ["KnudeKode"], "1"))}
${el("Bundkote", item.z.toFixed(2))}
${el("Terraenkote", resolveField(a, ["Terraenkote", "Terraen"], item.z.toFixed(2)))}
${el("XKoordinat", item.x.toFixed(2))}
${el("YKoordinat", item.y.toFixed(2))}
${el("FormKode", resolveField(a, ["FormKode", "Form"], "1"))}
${el("MaterialeKode", resolveField(a, ["MaterialeKode", "Materiale"], "1"))}
${el("DiameterBredde", resolveField(a, ["DiameterBredde", "Diameter"], null))}
${el("StatusKode", resolveField(a, ["StatusKode", "Status"], "1"))}
  </Knude>`;
}

export function buildKnudeGroupXml(family, items){
	let body = items.map(item => knudeElementXml(family, item)).join("\n");
	let ns = family === "danvand" ? "http://danva.dk/xml/schemas/van/2010/02/08" : "http://www.danva.dk/xml/schemas/dandas/20140701";

	return `<?xml version="1.0" encoding="utf-8"?>
<!--DANVA-fil eksporteret fra PointView (digitaliserede knuder).-->
<KnudeGroup xmlns="${ns}">
  <Referencesys>
    <KoordinatsysKode>9</KoordinatsysKode>
    <KotesysKode>1</KotesysKode>
  </Referencesys>
${body}
</KnudeGroup>
`;
}

// item: {knude1, knude2, midCoords: [[x,y,z],...] (excluding the two end
// knude themselves), diameterMm, attributes}
function ledningElementXml(family, item){
	let a = item.attributes;

	if(family === "danvand"){
		let punkter = item.midCoords.map((c, i) => `        <Punkt Sortering="${i + 1}">
          <XKoordinat>${c[0].toFixed(2)}</XKoordinat>
          <YKoordinat>${c[1].toFixed(2)}</YKoordinat>
          <Kote>${c[2].toFixed(2)}</Kote>
        </Punkt>`).join("\n");

		return `  <Ledning Knude1="${escapeXml(item.knude1)}" Knude2="${escapeXml(item.knude2)}" Loebenr="1">
${el("KategoriKode", resolveField(a, ["KategoriKode", "Kategori"], "2"))}
${el("StatusKode", resolveField(a, ["StatusKode", "Status"], "2"))}
${el("EjerforholdKode", resolveField(a, ["EjerforholdKode", "Ejerforhold"], "10"))}
${el("DatoEtableret", resolveField(a, ["DatoEtableret", "Dato"], NOW_ISO))}
${el("Sagsnummer", resolveField(a, ["Sagsnummer", "Sag"], null))}
${el("KalibreretDim", item.diameterMm ? Math.round(item.diameterMm) : resolveField(a, ["KalibreretDim", "Diameter"], null))}
    <NoteItems />
    <PunktItems>
${punkter}
    </PunktItems>
    <KnudePaaLedningItems />
  </Ledning>`;
	}

	// dandas - one DelLedning per Ledning in this v1 (no support for
	// exporting a multi-segment Ledning back out, matching the fact that a
	// digitized line here is drawn as a single continuous run).
	let knaekker = item.midCoords.map((c, i) => `          <Knaekpunkt Sortering="${i + 1}">
            <XKoordinat>${c[0].toFixed(2)}</XKoordinat>
            <YKoordinat>${c[1].toFixed(2)}</YKoordinat>
          </Knaekpunkt>`).join("\n");
	let zStart = item.startZ, zEnd = item.endZ;

	return `  <Ledning>
    <DelLedningItems>
      <DelLedning OpstroemKnudenavn="${escapeXml(item.knude1)}" NedstroemKnudenavn="${escapeXml(item.knude2)}">
${el("BundloebskoteOpst", zStart.toFixed(2))}
${el("BundloebskoteNedst", zEnd.toFixed(2))}
${el("DiameterIndv", item.diameterMm ? Math.round(item.diameterMm) : resolveField(a, ["DiameterIndv", "Diameter"], null))}
${el("MaterialeKode", resolveField(a, ["MaterialeKode", "Materiale"], "1"))}
${el("DatoEtableret", resolveField(a, ["DatoEtableret", "Dato"], NOW_ISO))}
        <KnaekpunktItems>
${knaekker}
        </KnaekpunktItems>
      </DelLedning>
    </DelLedningItems>
  </Ledning>`;
}

export function buildLedningGroupXml(family, items){
	let body = items.map(item => ledningElementXml(family, item)).join("\n");
	let ns = family === "danvand" ? "http://danva.dk/xml/schemas/van/2010/02/08" : "http://www.danva.dk/xml/schemas/dandas/20140701";

	return `<?xml version="1.0" encoding="utf-8"?>
<!--DANVA-fil eksporteret fra PointView (digitaliserede ledninger).-->
<LedningGroup xmlns="${ns}">
  <Referencesys>
    <KoordinatsysKode>9</KoordinatsysKode>
    <KotesysKode>1</KotesysKode>
  </Referencesys>
${body}
</LedningGroup>
`;
}

// DANVAND only. items grouped by knudenavn -> [{subtype, attributes}, ...]
// (a knude can carry several components, e.g. both a Ventil and a
// Maalerenhed) - see groupKomponentItemsByKnude below for building this
// from a flat instance list.
export function buildKnudeKomponentGroupXml(itemsByKnude){
	let body = [...itemsByKnude.entries()].map(([knudenavn, subItems]) => {
		let bySubtype = new Map();
		for(let {subtype} of subItems){
			bySubtype.set(subtype, (bySubtype.get(subtype) || 0) + 1);
		}

		// Every KnudeKomponent must list all ten *Items containers even
		// when empty (see the real example data - every knude repeats the
		// full, mostly-empty set) - only the ones this knude actually has
		// get a populated inner element, matching what a real EnkelSamling
		// (etc.) needs at minimum: just enough to exist as a record, since
		// this v1 doesn't collect per-subtype attribute detail beyond
		// whatever the digitized feature's own attributes provide.
		let allSubtypes = ["Beholder", "Boring", "EnkelSamling", "KombiKomp", "Maalerenhed", "Forbrugssted", "Pumpe", "Reservoir", "Tapsted", "Ventil"];
		let itemsXml = allSubtypes.map(subtype => {
			let count = bySubtype.get(subtype) || 0;
			if(count === 0){
				return `    <${subtype}Items />`;
			}
			let entries = Array(count).fill(`      <${subtype}>
        <StatusKode>2</StatusKode>
      </${subtype}>`).join("\n");
			return `    <${subtype}Items>
${entries}
    </${subtype}Items>`;
		}).join("\n");

		return `  <KnudeKomponent Knudenavn="${escapeXml(knudenavn)}">
${itemsXml}
  </KnudeKomponent>`;
	}).join("\n");

	return `<?xml version="1.0" encoding="utf-8"?>
<!--DANVA-fil eksporteret fra PointView (digitaliserede komponenter).-->
<KnudeKomponentGroup xmlns="http://danva.dk/xml/schemas/van/2010/02/08">
${body}
</KnudeKomponentGroup>
`;
}

// Groups a flat list of {knudenavn, subtype, attributes} component
// instances by knudenavn, the shape buildKnudeKomponentGroupXml expects.
export function groupKomponentItemsByKnude(items){
	let map = new Map();
	for(let item of items){
		if(!map.has(item.knudenavn)){
			map.set(item.knudenavn, []);
		}
		map.get(item.knudenavn).push(item);
	}
	return map;
}

// ── "Samling"/WKT generation (DANVAND 2.1.1, DANDAS 3.1.1) ─────────────────
//
// Mirrors the Group-generation builders above field-for-field where a
// direct equivalent exists, but namespace-qualifies every element per its
// OWN declaring schema (a real requirement here - unlike the older Group
// documents, elementFormDefault="qualified" plus per-element namespaces
// means getting this wrong per element is the single easiest way to fail
// XSD validation, exactly the pitfall the parallel DANVAND 2.0.0 exporter
// elsewhere in this codebase documents hitting for the same schema
// family): Knude/Ledning/KnudeSamling/LedningSamling/FunktionStruktur (+
// its role choice) belong to the van/das namespace (declared directly in
// DANVAND_2.1.1/DANDAS_3.1.1's own schemas), while Geometri/Terraenkote/
// DatoOprettetGeometri/Loebenr/DatoOprettet/ReferencesystemStruktur belong
// to the shared ker namespace (declared in kerne's base KnudeType/
// LinieType). guid/navn/startknudenavn/etc. are XML ATTRIBUTES, never
// namespace-prefixed (attributeFormDefault="unqualified").
const SAMLING_NS = {
	danvand: {prefix: "van", uri: "urn:danva:van:1.1.2"},
	dandas: {prefix: "das", uri: "urn:danva:das:1.1.1"},
};
const KER_NS_URI = "urn:danva:ker:1.1.2";

// item: {knudenavn, guid?, x, y, z, subtype?, attributes}
function knudeSamlingElementXml(family, item, guidsByKnudenavn){
	let a = item.attributes;
	let ns = SAMLING_NS[family].prefix;
	let guid = ensureDanvaGuid(guidsByKnudenavn, item.knudenavn, item.guid);
	let subtype = item.subtype || DEFAULT_SAMLING_SUBTYPE[family];
	let terraenkote = resolveField(a, ["Terraenkote", "Terraen"], null);

	return `    <${ns}:Knude guid="${guid}" navn="${escapeXml(item.knudenavn)}">
      <ker:DatoOprettetGeometri>${resolveField(a, ["DatoOprettetGeometri", "DatoOprettet", "Dato"], NOW_DATE)}</ker:DatoOprettetGeometri>
      <ker:Geometri>${buildWktPoint([item.x, item.y, item.z])}</ker:Geometri>
${terraenkote !== null ? `      <ker:Terraenkote>${Number(terraenkote).toFixed(2)}</ker:Terraenkote>\n` : ""}      <${ns}:FunktionStruktur>
        <${ns}:${subtype}/>
      </${ns}:FunktionStruktur>
    </${ns}:Knude>`;
}

// Root-level Samling document wrapper - shared shape for both Knude/
// Ledning Samling documents (only the root tag, per-element prefix and the
// per-item element builder differ).
function buildSamlingXml(family, rootTag, kindLabel, bodyXml){
	let ns = SAMLING_NS[family];

	return `<?xml version="1.0" encoding="utf-8"?>
<!--DANVA-fil eksporteret fra PointView (digitaliserede ${kindLabel}, ${family === "danvand" ? "DANVAND 2.1.1" : "DANDAS 3.1.1"}).-->
<${ns.prefix}:${rootTag} xmlns:${ns.prefix}="${ns.uri}" xmlns:ker="${KER_NS_URI}">
  <ker:ReferencesystemStruktur/>
${bodyXml}
</${ns.prefix}:${rootTag}>
`;
}

export function buildKnudeSamlingXml(family, items){
	let guidsByKnudenavn = new Map();
	let body = items.map(item => knudeSamlingElementXml(family, item, guidsByKnudenavn)).join("\n");
	return buildSamlingXml(family, "KnudeSamling", "knuder", body);
}

// item: {knude1, knude1Guid?, knude2, knude2Guid?, coords: [[x,y,z], ...]
// (full line INCLUDING both end points, unlike the Group builder's
// midCoords-only shape - Samling's own Geometri is one complete WKT
// LineString), diameterMm, attributes}
function ledningSamlingElementXml(family, item, guidsByKnudenavn){
	let a = item.attributes;
	let ns = SAMLING_NS[family].prefix;
	let guid = randomGuid(); // the Ledning's own identity - never referenced again, so no need to cache/dedupe it
	let g1 = ensureDanvaGuid(guidsByKnudenavn, item.knude1, item.knude1Guid);
	let g2 = ensureDanvaGuid(guidsByKnudenavn, item.knude2, item.knude2Guid);

	return `    <${ns}:Ledning guid="${guid}" startknudeGuid="${g1}" startknudenavn="${escapeXml(item.knude1)}" slutknudeGuid="${g2}" slutknudenavn="${escapeXml(item.knude2)}">
      <ker:DatoOprettetGeometri>${resolveField(a, ["DatoOprettetGeometri", "DatoOprettet", "Dato"], NOW_DATE)}</ker:DatoOprettetGeometri>
      <ker:Geometri>${buildWktLineString(item.coords)}</ker:Geometri>
      <ker:Loebenr>1</ker:Loebenr>
      <ker:DatoOprettet>${resolveField(a, ["DatoOprettet", "Dato"], NOW_DATE)}</ker:DatoOprettet>
${el("ker:StatusKode", resolveField(a, ["StatusKode", "Status"], "2"))}
    </${ns}:Ledning>`;
}

export function buildLedningSamlingXml(family, items){
	let guidsByKnudenavn = new Map();
	let body = items.map(item => ledningSamlingElementXml(family, item, guidsByKnudenavn)).join("\n");
	return buildSamlingXml(family, "LedningSamling", "ledninger", body);
}
